import { v4 as uuid } from "uuid";
import { ChangedCells } from "@components/spreadsheet/SpreadsheetGrid";
import { SpreadsheetData } from "@components/spreadsheet/types";
import { EliminatingEntryLineForm } from "./eliminatingEntryDrawer.lib";

interface AccountReference {
    id: string;
}

interface AccountWithCode extends AccountReference {
    code: number;
}

interface ApplyGridChangeParams {
    newData: SpreadsheetData;
    changedCells: ChangedCells;
    columnFields: readonly string[];
    priorLines: EliminatingEntryLineForm[];
    accountsById: Record<string, AccountWithCode>;
    accountsByEntityId: Record<number, readonly AccountReference[]>;
}

export function getEntityScopedAccounts<T extends AccountReference>(
    entityId: number | null,
    accountsByEntityId: Record<number, T[]>,
): T[] {
    return entityId === null ? [] : (accountsByEntityId[entityId] ?? []);
}

function numberOrUndefined(
    value: SpreadsheetData[number][string],
): number | undefined {
    return typeof value === "number" && Number.isFinite(value)
        ? value
        : undefined;
}

function stringArray(value: SpreadsheetData[number][string]): string[] {
    return Array.isArray(value)
        ? value.filter((item) => typeof item === "string")
        : [];
}

export function toEliminatingGridData(
    lines: EliminatingEntryLineForm[],
): SpreadsheetData {
    return lines.map((line) => ({
        clientId: line.clientId,
        entityId: line.entityId,
        accountId: line.accountId,
        counterpartyId: line.counterpartyId,
        classIds: line.classIds,
        debitAmount: line.debitAmount ?? null,
        creditAmount: line.creditAmount ?? null,
        excludeFrom1099: line.excludeFrom1099,
        description: line.description,
    }));
}

export function applyEliminatingGridChange({
    newData,
    changedCells,
    columnFields,
    priorLines,
    accountsById,
    accountsByEntityId,
}: ApplyGridChangeParams): EliminatingEntryLineForm[] {
    const changedFieldsByRow = new Map<number, Set<string>>();
    for (const [rowIndex, columnIndex] of changedCells) {
        const field = columnFields[columnIndex];
        if (!field) {
            continue;
        }
        const changedFields =
            changedFieldsByRow.get(rowIndex) ?? new Set<string>();
        changedFields.add(field);
        changedFieldsByRow.set(rowIndex, changedFields);
    }

    const priorLinesByClientId = new Map(
        priorLines.map((line) => [line.clientId, line]),
    );

    return newData.map((row, rowIndex) => {
        const clientId =
            typeof row.clientId === "string" ? row.clientId : uuid();
        const priorLine = priorLinesByClientId.get(clientId);
        const entityId = typeof row.entityId === "number" ? row.entityId : null;
        const rawAccountId =
            typeof row.accountId === "string" ? row.accountId : null;
        const changedFields = changedFieldsByRow.get(rowIndex);
        const entityAccounts =
            entityId === null ? undefined : accountsByEntityId[entityId];
        const isAccountOutsideEntity =
            changedFields?.has("entityId") === true &&
            rawAccountId !== null &&
            entityAccounts !== undefined &&
            !entityAccounts.some((account) => account.id === rawAccountId);
        const accountId = isAccountOutsideEntity ? null : rawAccountId;
        const account = accountId ? accountsById[accountId] : undefined;
        const accountCode =
            account?.code ??
            (accountId === priorLine?.accountId
                ? (priorLine.accountCode ?? null)
                : null);

        let debitAmount = numberOrUndefined(row.debitAmount);
        let creditAmount = numberOrUndefined(row.creditAmount);
        if (changedFields?.has("debitAmount") && (debitAmount ?? 0) > 0) {
            creditAmount = undefined;
        }
        if (changedFields?.has("creditAmount") && (creditAmount ?? 0) > 0) {
            debitAmount = undefined;
        }

        return {
            clientId,
            entityId,
            accountId,
            accountCode,
            debitAmount,
            creditAmount,
            counterpartyId:
                typeof row.counterpartyId === "string"
                    ? row.counterpartyId
                    : null,
            classIds: stringArray(row.classIds),
            excludeFrom1099:
                typeof row.excludeFrom1099 === "boolean"
                    ? row.excludeFrom1099
                    : false,
            description:
                typeof row.description === "string" ? row.description : "",
        };
    });
}
